/**
 * The paper review's reference in the URL (?paper=<ref>) and what the review
 * derives from a response (GET …/activity/papers/<attempt_id> or
 * …/papers/app/<session_id>; student_insights/types.py PaperReviewResponse).
 *
 * A ref is the part's quiz_attempts id, or "app:<session id>" for a part
 * whose source is "app_session" (a pathbuddy.app session, which has its own
 * endpoint).
 */
import { clockLabel, minutesLabel, paperScore } from './activityFormat';

const APP_PREFIX = 'app:';

/** The ?paper= value that opens a part: { id, source } from a PaperRow's parts or sibling_parts. */
export function paperRefFor(part) {
  if (!part || !part.id) return null;
  return part.source === 'app_session' ? `${APP_PREFIX}${part.id}` : String(part.id);
}

/** { source: 'attempt'|'app_session', id } for a ?paper= value; null when it names nothing. */
export function parsePaperRef(ref) {
  const text = ref == null ? '' : String(ref).trim();
  if (!text) return null;
  if (text.startsWith(APP_PREFIX)) {
    const id = text.slice(APP_PREFIX.length).trim();
    return id ? { source: 'app_session', id } : null;
  }
  return { source: 'attempt', id: text };
}

/** Questions with a right or wrong answer: what "Correct n / total" counts. Essays and unrecorded .app answers are not marked. */
export const markedCount = (summary) => summary.correct + summary.wrong + summary.unanswered;

/** Questions to look at again: wrong or not answered. */
export const needsLookCount = (summary) => summary.wrong + summary.unanswered;

/** A part's score over its marked questions, by the Exam practice rows' rule (activityFormat.paperScore). */
export const reviewScore = (summary) => paperScore(
  summary.correct, markedCount(summary), summary.correct + summary.wrong,
);

/**
 * A part tab's score: "13 / 20 correct · 15 answered · 86.7% of those",
 * "Not marked" (an essay) or "Answers not recorded" (.app).
 */
export function partScore(summary) {
  if (markedCount(summary) > 0) {
    const score = reviewScore(summary);
    return `${score.main} · ${score.detail}`;
  }
  if (summary.not_marked > 0) return 'Not marked';
  if (summary.not_recorded > 0) return 'Answers not recorded';
  return 'No questions';
}

/** A part tab's caption: its score, then its time when one was recorded. */
export function partCaption(review) {
  return [partScore(review.summary), minutesLabel(review.attempt.duration_seconds)].filter(Boolean).join(' · ');
}

/** The time a question took, or null (.com attempts do not record it). */
export const questionTime = (question) => clockLabel(question.time_spent_seconds);

/** The question grid's mark for an outcome: r(ight), w(rong), u(nanswered), or n(either). */
export function gridTone(outcome) {
  if (outcome === 'correct') return 'r';
  if (outcome === 'wrong') return 'w';
  if (outcome === 'unanswered') return 'u';
  return 'n';
}

/** Whether a question is shown under "Wrong or not answered". */
export const needsLook = (question) => question.outcome === 'wrong' || question.outcome === 'unanswered';

/**
 * The questions to show, each with the passage to draw above it. An LNAT
 * passage comes with every one of its questions; it is drawn once, above
 * the first shown question of a run that shares it.
 */
export function withPassages(questions) {
  let previous = null;
  return questions.map((question) => {
    const exhibit = question.exhibit && question.exhibit.text ? question.exhibit : null;
    const key = exhibit ? `${exhibit.is_html ? 'h' : 't'}:${exhibit.text}` : null;
    const passage = key && key !== previous ? exhibit : null;
    previous = key;
    return { question, passage };
  });
}

/** Where a sitting's parts came from, for the header: one sitting, grouped by time, or a single part. */
export function sittingNote(attempt) {
  const parts = (attempt.sibling_parts || []).length;
  if (parts < 2) return null;
  if (attempt.grouped_by === 'sitting_id') {
    return { recorded: true, text: parts === 2 ? 'both papers in one sitting' : `all ${parts} parts in one sitting` };
  }
  return { recorded: false, text: 'Parts grouped by time, not recorded as one sitting' };
}
